<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import { Entitlement, PropertyKind, Subscription } from "~/shared/enums";
import type { PropertyConfig, PropertyDefaultKind, TaskKind } from "~/shared/types";
import { useDataStore, useTenantStore } from "~/stores";
import { getOrdersBetween } from "~/utils/orderManager";

import SettingsTable from "./SettingsTable.vue";
import TypeTableItem from "./TypeTableItem.vue";

defineProps<{
  property: PropertyDefaultKind;
  propertyConfig: PropertyConfig<PropertyKind.DEFAULT_KIND>;
  scrollContainer: HTMLElement | null;
}>();

const dataStore = useDataStore();
const tenantStore = useTenantStore();

const tableRef = ref<ComponentExposed<typeof SettingsTable> | null>(null);

const taskKinds = computed(() => dataStore.taskKindList);

const maxTaskKinds = computed(() => tenantStore.getEntitlementValue(Entitlement.MAX_TASK_KINDS) as number);

const getComponentProps = (taskKind: TaskKind, index: number) => ({
  taskKind,
  isFirst: index === 0,
});

const onDragChange = (_category: string, taskKind: TaskKind) => {
  dataStore.updateTaskKind({ duid: taskKind.duid, order: taskKind.order });
};

const createTaskKind = async () => {
  const firstKind = taskKinds.value[0];
  const order = getOrdersBetween(undefined, firstKind?.order)[0];

  const newKind = await dataStore.createTaskKind("", order);
  if (!newKind) {
    return;
  }

  await nextTick();
  const itemRefs = tableRef.value?.itemRefs as InstanceType<typeof TypeTableItem>[] | undefined;
  const newItemRef = itemRefs?.find((item) => item.duid === newKind.duid);
  newItemRef?.startEditing();
};
</script>

<template>
  <SettingsTable
    ref="tableRef"
    title="Types"
    :items="taskKinds"
    :item-component="TypeTableItem"
    :get-component-props="getComponentProps"
    :on-create="createTaskKind"
    :on-change="onDragChange"
    drag-group="task-kinds"
    drag-category="task-kinds"
    :max-items="maxTaskKinds"
    :upgrade-subscription="Subscription.PREMIUM"
    upgrade-feature-action="create more than the allowed number of types" />
</template>
